import type { ArtifactOperation, ArtifactOperationType } from "@virtual-brain-lib/types";

const OPERATION_STALE_AFTER_MS = 15 * 60 * 1_000;

/**
 * A row can stay "processing" forever when its process dies before settling it (SIGKILL, OOM).
 * Ageing those out keeps a dead row from locking the artifact UI and the API conflict guards.
 */
function isOperationActive(operation: ArtifactOperation): boolean {
	if (operation.status !== "processing") {
		return false;
	}

	return Date.now() - new Date(operation.startedAt).getTime() < OPERATION_STALE_AFTER_MS;
}

export function getArtifactFirstProcessingOperation(
	operations: ArtifactOperation[] | undefined,
): ArtifactOperation | undefined {
	return operations?.find(isOperationActive);
}

export function hasArtifactProcessingOperation(
	operations: ArtifactOperation[] | undefined,
): boolean {
	return getArtifactFirstProcessingOperation(operations) !== undefined;
}

export function hasArtifactProcessingOperationOfType({
	operations,
	type,
}: {
	operations: ArtifactOperation[] | undefined;
	type: ArtifactOperationType;
}): boolean {
	return (
		operations?.some((operation) => isOperationActive(operation) && operation.type === type) ??
		false
	);
}

export function getArtifactLastOperation(
	operations: ArtifactOperation[] | undefined,
): ArtifactOperation | undefined {
	if (!operations || operations.length === 0) {
		return undefined;
	}

	return operations.reduce((latest, operation) =>
		new Date(operation.startedAt).getTime() > new Date(latest.startedAt).getTime()
			? operation
			: latest,
	);
}
